import {
  CreateListingInputSchema,
  ListingCurrencySchema,
  type CreateListingInput,
  type Listing,
  type ListingCurrency,
} from '@carsfinders/contracts';
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { createSellerListing, updateSellerListing } from './api';

/** `CreateListingInputSchema.photos` caps the array at 50. */
export const MAX_LISTING_PHOTOS = 50;

const CURRENCIES = ListingCurrencySchema.options;

const FIELD = 'h-10 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-sm';
const TEXTAREA = 'w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 py-2 text-sm';
const LABEL = 'mb-1 block font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase';

/** The form's raw string state — what the inputs hold before validation. */
export interface ListingFormValues {
  title: string;
  brand: string;
  model: string;
  year: string;
  price: string;
  currency: ListingCurrency;
  mileage: string;
  vin: string;
  location: string;
  description: string;
  damageDisclosure: string;
  /** Photo URLs, one per line — the first one is the main photo. */
  photos: string;
}

/** Keyed by payload field, except the title, which is `listingTitle` like its i18n key. */
export type ListingFieldErrors = Partial<
  Record<Exclude<keyof CreateListingInput, 'title'> | 'listingTitle', string>
>;

const EMPTY: ListingFormValues = {
  title: '',
  brand: '',
  model: '',
  year: '',
  price: '',
  currency: 'EUR',
  mileage: '',
  vin: '',
  location: '',
  description: '',
  damageDisclosure: '',
  photos: '',
};

/** A LocalizedString is either plain text or an { en, pl, ua } object. */
function plainDescription(value: Listing['description']): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return value.ua ?? value.en ?? value.pl ?? '';
}

/** Start an edit from the saved listing — photos included, or the PATCH would wipe them. */
export function listingToFormValues(listing: Listing): ListingFormValues {
  return {
    title: listing.title,
    brand: listing.brand,
    model: listing.model,
    year: String(listing.year),
    price: String(listing.price),
    currency: listing.currency,
    mileage: String(listing.mileage),
    vin: listing.vin ?? '',
    location: listing.location ?? '',
    description: plainDescription(listing.description),
    damageDisclosure: listing.damageDisclosure ?? '',
    photos: (listing.photos ?? []).join('\n'),
  };
}

/**
 * Photo URLs from the textarea, one per line: blanks and repeats dropped, order
 * kept — the same rule the marketplace's «Додати за URL» fallback applies.
 */
export function parsePhotoUrls(text: string): string[] {
  const seen = new Set<string>();
  for (const line of text.split('\n')) {
    const url = line.trim();
    if (url) seen.add(url);
  }
  return [...seen];
}

export type ListingValidation =
  { ok: true; payload: CreateListingInput } | { ok: false; errors: ListingFieldErrors };

/**
 * Raw inputs → a consignment `CreateListingInput`, mirroring
 * `CreateListingInputSchema` so the form refuses what the API would 400 on.
 * The schema itself is the final guard; the API stays the source of truth.
 */
export function validateListingForm(values: ListingFormValues): ListingValidation {
  const title = values.title.trim();
  const year = Number.parseInt(values.year, 10);
  const price = values.price.trim() === '' ? Number.NaN : Number(values.price);
  const mileage = Number.parseInt(values.mileage, 10);
  const vin = values.vin.trim();
  const photos = parsePhotoUrls(values.photos);

  const errors: ListingFieldErrors = {};
  if (title.length < 2 || title.length > 200) {
    errors.listingTitle = t('marketplace.seller.form.errors.listingTitle');
  }
  if (!values.brand.trim()) errors.brand = t('marketplace.seller.form.errors.brand');
  if (!values.model.trim()) errors.model = t('marketplace.seller.form.errors.model');
  if (!Number.isFinite(year) || year < 1900 || year > 2100) {
    errors.year = t('marketplace.seller.form.errors.year');
  }
  if (!Number.isFinite(price) || price < 0) {
    errors.price = t('marketplace.seller.form.errors.price');
  }
  if (!Number.isFinite(mileage) || mileage < 0) {
    errors.mileage = t('marketplace.seller.form.errors.mileage');
  }
  if (vin && (vin.length < 11 || vin.length > 17)) {
    errors.vin = t('marketplace.seller.form.errors.vin');
  }
  if (photos.length > MAX_LISTING_PHOTOS) {
    errors.photos = t('marketplace.admin.seller.photosTooMany', { max: MAX_LISTING_PHOTOS });
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const parsed = CreateListingInputSchema.safeParse({
    listingMode: 'consignment',
    title,
    brand: values.brand.trim(),
    model: values.model.trim(),
    year,
    price,
    currency: values.currency,
    mileage,
    vin: vin || undefined,
    photos,
    description: values.description.trim() || undefined,
    location: values.location.trim() || undefined,
    damageDisclosure: values.damageDisclosure.trim() || undefined,
  });
  if (!parsed.success)
    return { ok: false, errors: toFieldErrors(parsed.error.flatten().fieldErrors) };
  return { ok: true, payload: parsed.data };
}

/** zod's flattened `fieldErrors` (client guard or the API's 400 `details`) → the form's map. */
function toFieldErrors(fieldErrors: Record<string, string[] | undefined>): ListingFieldErrors {
  const errors: Record<string, string> = {};
  for (const [key, messages] of Object.entries(fieldErrors)) {
    const message = messages?.[0];
    if (message) errors[key === 'title' ? 'listingTitle' : key] = message;
  }
  return errors as ListingFieldErrors;
}

/** The API answers a schema failure with 400 `{ error, details: { fieldErrors } }`. */
function serverFieldErrors(error: unknown): ListingFieldErrors | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  const details = (error.body as { details?: { fieldErrors?: unknown } } | undefined)?.details;
  const fieldErrors = details?.fieldErrors;
  if (!fieldErrors || typeof fieldErrors !== 'object') return null;
  return toFieldErrors(fieldErrors as Record<string, string[] | undefined>);
}

export interface SellerListingFormProps {
  /** When set the form PATCHes this draft/rejected listing instead of creating one. */
  editing: Listing | null;
  onSaved: (listing: Listing) => void;
  onCancel: () => void;
}

/**
 * Consignment listing form → POST /api/marketplace/listings (create) or PATCH
 * /api/marketplace/listings/:id (edit). Only `consignment` is created here:
 * an own_stock listing comes from a won car on the «Власний склад» tab.
 */
export function SellerListingForm({ editing, onSaved, onCancel }: SellerListingFormProps) {
  const [values, setValues] = useState<ListingFormValues>(() =>
    editing ? listingToFormValues(editing) : EMPTY,
  );
  const [fieldErrors, setFieldErrors] = useState<ListingFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (payload: CreateListingInput) => {
      if (!editing) return createSellerListing(payload);
      // listingMode is immutable after creation — the PATCH schema omits it.
      const { listingMode: _mode, ...patch } = payload;
      return updateSellerListing(editing.listingId, patch);
    },
    onSuccess: (result) => {
      toast.success(t('marketplace.seller.form.success'));
      onSaved(result.listing);
    },
    onError: (error: unknown) => {
      const fromServer = serverFieldErrors(error);
      if (fromServer) setFieldErrors(fromServer);
      setFormError(t('marketplace.seller.form.error'));
    },
  });

  const set = <K extends keyof ListingFormValues>(key: K, value: ListingFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const result = validateListingForm(values);
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    saveMutation.mutate(result.payload);
  };

  const error = (id: string, message: string | undefined) =>
    message ? (
      <p className="mt-1 text-[13px] text-cf-danger" data-testid={`listing-error-${id}`}>
        {message}
      </p>
    ) : null;

  const input = (
    id: string,
    key: Exclude<keyof ListingFormValues, 'currency'>,
    labelKey: string,
    message: string | undefined,
    options: { type?: 'text' | 'number'; required?: boolean } = {},
  ) => (
    <div>
      <label htmlFor={id} className={LABEL}>
        {t(labelKey)}
        {options.required ? ' *' : ''}
      </label>
      <input
        id={id}
        type={options.type ?? 'text'}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
        className={options.type === 'number' ? `${FIELD} font-mono` : FIELD}
        aria-invalid={Boolean(message)}
      />
      {error(id, message)}
    </div>
  );

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="listing-form"
      className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-3.5 lg:px-5 lg:py-[18px]"
    >
      <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
        {editing ? editing.title : t('marketplace.seller.form.title')}
      </h2>
      <p className="mt-1 mb-4 text-xs leading-[1.4] text-cf-ink-subtle lg:text-[13px]">
        {t('marketplace.seller.form.subtitle')}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          {input(
            'listing-title',
            'title',
            'marketplace.seller.form.listingTitle',
            fieldErrors.listingTitle,
            {
              required: true,
            },
          )}
        </div>
        {input('listing-brand', 'brand', 'marketplace.seller.form.brand', fieldErrors.brand, {
          required: true,
        })}
        {input('listing-model', 'model', 'marketplace.seller.form.model', fieldErrors.model, {
          required: true,
        })}
        {input('listing-year', 'year', 'marketplace.seller.form.year', fieldErrors.year, {
          type: 'number',
          required: true,
        })}
        {input(
          'listing-mileage',
          'mileage',
          'marketplace.seller.form.mileage',
          fieldErrors.mileage,
          {
            type: 'number',
            required: true,
          },
        )}
        {input('listing-price', 'price', 'marketplace.seller.form.price', fieldErrors.price, {
          type: 'number',
          required: true,
        })}
        <div>
          <label htmlFor="listing-currency" className={LABEL}>
            {t('marketplace.seller.form.currency')}
          </label>
          <select
            id="listing-currency"
            value={values.currency}
            onChange={(event) => set('currency', event.target.value as ListingCurrency)}
            className={FIELD}
          >
            {CURRENCIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
        {input('listing-vin', 'vin', 'marketplace.seller.form.vin', fieldErrors.vin)}
        {input(
          'listing-location',
          'location',
          'marketplace.seller.form.location',
          fieldErrors.location,
        )}

        <div className="sm:col-span-2">
          <label htmlFor="listing-photos" className={LABEL}>
            {t('marketplace.seller.form.photos')}
          </label>
          <textarea
            id="listing-photos"
            value={values.photos}
            onChange={(event) => set('photos', event.target.value)}
            rows={3}
            className={`${TEXTAREA} font-mono text-[13px]`}
            aria-invalid={Boolean(fieldErrors.photos)}
          />
          <p className="mt-1 text-xs text-cf-ink-subtle">{t('marketplace.seller.photos.hint')}</p>
          {error('listing-photos', fieldErrors.photos)}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="listing-description" className={LABEL}>
            {t('marketplace.seller.form.description')}
          </label>
          <textarea
            id="listing-description"
            value={values.description}
            onChange={(event) => set('description', event.target.value)}
            rows={4}
            className={TEXTAREA}
          />
          {error('listing-description', fieldErrors.description)}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="listing-damage" className={LABEL}>
            {t('marketplace.seller.form.damageDisclosure')}
          </label>
          <textarea
            id="listing-damage"
            value={values.damageDisclosure}
            onChange={(event) => set('damageDisclosure', event.target.value)}
            rows={3}
            className={TEXTAREA}
          />
          {error('listing-damage', fieldErrors.damageDisclosure)}
        </div>
      </div>

      {formError && (
        <p
          className="mt-3 text-[13px] text-cf-danger"
          role="alert"
          data-testid="listing-form-error"
        >
          {formError}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={saveMutation.isPending}
          data-testid="listing-form-submit"
          className="h-11 w-full rounded-cf-md bg-cf-brand px-5 text-sm font-semibold text-white hover:bg-cf-brand-hover disabled:opacity-50 sm:w-auto lg:h-10"
        >
          {saveMutation.isPending
            ? t(editing ? 'marketplace.seller.form.saving' : 'marketplace.seller.form.submitting')
            : t(editing ? 'marketplace.seller.form.save' : 'marketplace.seller.form.submit')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-10 px-2 text-sm text-cf-ink-muted hover:text-cf-ink"
        >
          {t('marketplace.seller.form.cancel')}
        </button>
      </div>
    </form>
  );
}
